import React from "react";
import { cockpit } from "@/api";
import type { Fact, PreviousRequestSummary, RequestDetail } from "@/api";
import type { PushToast } from "@/notifications/toast-queue";
import { useT } from "../../i18n";
import { draftValueMarks, evidenceMarks } from "../evidence-highlight";
import { reconcileReviewMessageSelection, type ReviewTabId } from "./model";
import { findEvidenceMark } from "./use-evidence-selection";

interface WorkspaceHistoryOptions {
  requestId: string;
  canonicalIdRef: React.MutableRefObject<string | null>;
  editingRef: React.MutableRefObject<boolean>;
  pushToast: PushToast;
}

export function useWorkspaceHistory({
  requestId,
  canonicalIdRef,
  editingRef,
  pushToast,
}: WorkspaceHistoryOptions) {
  const t = useT();
  const tRef = React.useRef(t);
  const [previousRequests, setPreviousRequests] = React.useState<PreviousRequestSummary[] | null>(null);
  const [previousNextCursor, setPreviousNextCursor] = React.useState<string | null>(null);
  const [previousLoading, setPreviousLoading] = React.useState(false);
  const [previousError, setPreviousError] = React.useState<string | null>(null);
  const [historicalData, setHistoricalData] = React.useState<RequestDetail | null>(null);
  const [historicalLoadingId, setHistoricalLoadingId] = React.useState<string | null>(null);
  const [historicalSelectedMessageKey, setHistoricalSelectedMessageKey] = React.useState<string | null>(null);
  const [historicalReviewTab, setHistoricalReviewTab] = React.useState<ReviewTabId>("draft");
  const [historicalHoverMark, setHistoricalHoverMark] = React.useState<string | null>(null);
  const [historicalPinnedMark, setHistoricalPinnedMark] = React.useState<string | null>(null);

  React.useEffect(() => {
    tRef.current = t;
  }, [t]);

  React.useEffect(() => {
    setPreviousRequests(null);
    setPreviousNextCursor(null);
    setPreviousLoading(false);
    setPreviousError(null);
    setHistoricalData(null);
    setHistoricalLoadingId(null);
    setHistoricalSelectedMessageKey(null);
    setHistoricalReviewTab("draft");
    setHistoricalHoverMark(null);
    setHistoricalPinnedMark(null);
  }, [requestId]);

  const loadPreviousRequests = React.useCallback(async (force = false) => {
    if (!force && (previousRequests !== null || previousLoading)) return;
    setPreviousLoading(true);
    setPreviousError(null);
    try {
      const history = await cockpit.listPreviousRequests(canonicalIdRef.current ?? requestId);
      setPreviousRequests(history.previous_requests);
      setPreviousNextCursor(history.next_cursor);
    } catch (historyError: any) {
      setPreviousError(historyError?.message || tRef.current("ws.previous.loadError"));
    } finally {
      setPreviousLoading(false);
    }
  }, [canonicalIdRef, previousLoading, previousRequests, requestId]);

  const loadMorePreviousRequests = React.useCallback(async () => {
    if (!previousNextCursor || previousLoading) return;
    setPreviousLoading(true);
    setPreviousError(null);
    try {
      const history = await cockpit.listPreviousRequests(
        canonicalIdRef.current ?? requestId,
        previousNextCursor,
      );
      setPreviousRequests((current) => {
        const existing = new Set((current ?? []).map((item) => item.id));
        return [...(current ?? []), ...history.previous_requests.filter((item) => !existing.has(item.id))];
      });
      setPreviousNextCursor(history.next_cursor);
    } catch (historyError: any) {
      setPreviousError(historyError?.message || tRef.current("ws.previous.loadError"));
    } finally {
      setPreviousLoading(false);
    }
  }, [canonicalIdRef, previousLoading, previousNextCursor, requestId]);

  const openPreviousRequestsPanel = React.useCallback(() => {
    void loadPreviousRequests();
  }, [loadPreviousRequests]);

  const openPreviousRequest = React.useCallback(async (historicalRequestId: string) => {
    if (editingRef.current || historicalLoadingId !== null) return;
    setHistoricalLoadingId(historicalRequestId);
    try {
      const historical = await cockpit.getRequest(historicalRequestId);
      setHistoricalData(historical);
      setHistoricalSelectedMessageKey(reconcileReviewMessageSelection(historical.messages, null));
      setHistoricalReviewTab("draft");
      setHistoricalHoverMark(null);
      setHistoricalPinnedMark(null);
    } catch (historyError: any) {
      pushToast({
        tone: "danger",
        title: tRef.current("ws.previous.openFailed"),
        msg: historyError?.message || String(historyError),
      });
    } finally {
      setHistoricalLoadingId(null);
    }
  }, [editingRef, historicalLoadingId, pushToast]);

  const historicalMessages = historicalData?.messages ?? [];
  const historicalFacts = historicalData?.facts ?? [];
  const historicalMarks = evidenceMarks(historicalFacts);
  const historicalDraftMarks = draftValueMarks(historicalFacts);
  const historicalActiveMark = historicalHoverMark ?? historicalPinnedMark;
  const historicalMessagesNewestFirst = historicalMessages.slice().reverse();
  const historicalLatestInbound = historicalMessagesNewestFirst
    .find((message) => message.direction === "inbound") ?? null;
  const historicalLatestOutbound = historicalMessagesNewestFirst
    .find((message) => message.direction === "outbound") ?? null;
  const historicalDraftSender = historicalLatestOutbound?.fromAddr
    || historicalLatestInbound?.toAddrs?.find(Boolean)
    || t("common.noInfo");
  const historicalDraftRecipient = historicalLatestOutbound?.toAddrs?.find(Boolean)
    || historicalLatestInbound?.fromAddr
    || t("common.noInfo");

  const selectHistoricalFactEvidence = (fact: Fact) => {
    const sourceMessageId = fact.source_message_id ?? null;
    if (sourceMessageId && historicalMessages.some((message) => message.id === sourceMessageId)) {
      setHistoricalSelectedMessageKey(sourceMessageId);
    }
    setHistoricalPinnedMark(fact.fieldPath);
    window.requestAnimationFrame(() => {
      const view = document.querySelector('[data-testid="previous-request-view"]');
      const mark = findEvidenceMark(view, fact.fieldPath);
      mark?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        block: "center",
      });
    });
  };

  return {
    previousRequests,
    previousNextCursor,
    previousLoading,
    previousError,
    historicalData,
    historicalLoadingId,
    historicalSelectedMessageKey,
    historicalReviewTab,
    historicalMessages,
    historicalFacts,
    historicalMarks,
    historicalDraftMarks,
    historicalActiveMark,
    historicalDraftSender,
    historicalDraftRecipient,
    setHistoricalData,
    setHistoricalSelectedMessageKey,
    setHistoricalReviewTab,
    setHistoricalHoverMark,
    loadPreviousRequests,
    loadMorePreviousRequests,
    openPreviousRequestsPanel,
    openPreviousRequest,
    selectHistoricalFactEvidence,
  };
}
